import { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Box,
  InputBase,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Avatar,
  Tooltip,
  useMediaQuery,
  useTheme,
  Chip,
  Paper,
  alpha,
  CircularProgress,
} from "@mui/material";

import {
  Menu as MenuIcon,
  Search as SearchIcon,
  ShoppingCart as CartIcon,
  Favorite as FavoriteIcon,
  Person as PersonIcon,
  Home as HomeIcon,
  Category as CategoryIcon,
  LocalOffer as OfferIcon,
  ContactSupport as SupportIcon,
  Logout as LogoutIcon,
  Store as StoreIcon,
  Login as LoginIcon,
  Settings as SettingsIcon,
  History as HistoryIcon,
  Close as CloseIcon,
} from "@mui/icons-material";

import { styled } from "@mui/material/styles";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import axiosInstance from "../config/axios.config";
import config from "../config/api.config";
import { useAuth } from "../../context/AuthContext";
import LogoutButton from "./LogoutButton";

// Styled Components
const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: 50,
  backgroundColor: alpha(theme.palette.grey[100], 0.9),
  "&:hover": {
    backgroundColor: alpha(theme.palette.grey[200], 1),
    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
  },
  width: "100%",
  maxWidth: 550,
  margin: "0 auto",
  transition: theme.transitions.create(["background-color", "border"]),
  border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
  [theme.breakpoints.down("md")]: {
    maxWidth: "100%",
  },
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: theme.palette.primary.main,
  zIndex: 1,
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0, 1.25),
  },
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  width: "100%",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1.4, 1, 1.4, 0),
    paddingLeft: `calc(1em + ${theme.spacing(5)})`,
    paddingRight: theme.spacing(7),
    width: "100%",
    fontSize: "14px",
    fontWeight: 400,
    "&::placeholder": {
      color: theme.palette.grey[500],
      opacity: 1,
      fontWeight: 400,
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "13px",
      paddingLeft: `calc(0.8em + ${theme.spacing(4)})`,
      paddingRight: theme.spacing(4.5),
    },
  },
}));

const ClearButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  right: 45,
  top: "50%",
  transform: "translateY(-50%)",
  padding: 2,
  color: theme.palette.grey[500],
  "&:hover": {
    backgroundColor: alpha(theme.palette.grey[400], 0.2),
    color: theme.palette.grey[700],
  },
  [theme.breakpoints.down("sm")]: {
    right: 8,
  },
}));

const getFirstName = (fullName) => {
  if (!fullName) return "User";
  return fullName.split(" ")[0];
};

const Navbar = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const { user, isLoggedIn, loading } = useAuth();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [categoryAnchorEl, setCategoryAnchorEl] = useState(null);
  const [loginAnchorEl, setLoginAnchorEl] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [wishlistCount, setWishlistCount] = useState(0);
  const [fetchingCart, setFetchingCart] = useState(false);
  const [fetchingWishlist, setFetchingWishlist] = useState(false);

  useEffect(() => {
    const handleCartUpdate = () => {
      fetchCartCount();
    };
    window.addEventListener("cartUpdated", handleCartUpdate);
    window.addEventListener("storage", handleCartUpdate);
    return () => {
      window.removeEventListener("cartUpdated", handleCartUpdate);
      window.removeEventListener("storage", handleCartUpdate);
    };
  }, []);

  useEffect(() => {
    if (isLoggedIn && user) {
      fetchCartCount();
      fetchWishlistCount();
    } else {
      setCartCount(0);
      setWishlistCount(0);
    }
  }, [isLoggedIn, user]);

  const fetchCartCount = async () => {
    if (fetchingCart) return;
    setFetchingCart(true);
    try {
      const response = await axiosInstance.get(config.endpoints.cart);
      if (response.data?.status === "success") {
        const cart = response.data.data?.cart;
        const totalItems = cart?.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
        setCartCount(totalItems);
      }
    } catch (error) {
      console.error("Error fetching cart:", error.response?.status, error.message);
    } finally {
      setFetchingCart(false);
    }
  };

  const fetchWishlistCount = async () => {
    if (fetchingWishlist) return;
    setFetchingWishlist(true);
    try {
      const response = await axiosInstance.get(config.endpoints.profile);
      if (response.data?.status === "success") {
        const wishlist = response.data.data?.user?.wishlist || [];
        setWishlistCount(wishlist.length);
      }
    } catch (error) {
      console.error("Error fetching wishlist:", error.response?.status, error.message);
    } finally {
      setFetchingWishlist(false);
    }
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleLoginMenuOpen = (event) => {
    setLoginAnchorEl(event.currentTarget);
  };

  const handleCategoryMenuOpen = (event) => {
    setCategoryAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setCategoryAnchorEl(null);
    setLoginAnchorEl(null);
  };

  const handleLogoutClick = () => {
    handleMenuClose();
    setMobileMenuOpen(false);
  };

  useEffect(() => {
    if (!isLoggedIn) {
      setAnchorEl(null);
      setMobileMenuOpen(false);
    }
  }, [isLoggedIn]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchValue.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchValue)}`);
      setShowSuggestions(false);
    }
  };

  const handleClearSearch = () => {
    setSearchValue("");
    setShowSuggestions(false);
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchValue(suggestion);
    navigate(`/search?q=${encodeURIComponent(suggestion)}`);
    setShowSuggestions(false);
  };

  const navigateTo = (path) => {
    navigate(path);
    handleMenuClose();
    setMobileMenuOpen(false);
  };

  const categories = [
    { name: "Electronics", icon: <StoreIcon sx={{ fontSize: "18px" }} />, path: "/category/electronics" },
    { name: "Fashion", icon: <StoreIcon sx={{ fontSize: "18px" }} />, path: "/category/fashion" },
    { name: "Home & Furniture", icon: <StoreIcon sx={{ fontSize: "18px" }} />, path: "/category/home-furniture" },
    { name: "Books", icon: <StoreIcon sx={{ fontSize: "18px" }} />, path: "/category/books" },
    { name: "Sports", icon: <StoreIcon sx={{ fontSize: "18px" }} />, path: "/category/sports" },
    { name: "Toys", icon: <StoreIcon sx={{ fontSize: "18px" }} />, path: "/category/toys" },
  ];

  const popularSearches = ["Smartphones", "Laptops", "Shoes", "Books", "Headphones", "Smart Watches", "Gaming"];

  const userFirstName = user?.name ? getFirstName(user.name) : "User";
  const userInitial = user?.name ? user.name.charAt(0).toUpperCase() : "U";

  if (loading) {
    return (
      <AppBar position="sticky" color="default" elevation={0} sx={{ bgcolor: "#FFFFFF", borderBottom: "1px solid #E5E7EB" }}>
        <Toolbar sx={{ minHeight: "64px !important", justifyContent: "center" }}>
          <CircularProgress size={28} sx={{ color: "#3B82F6" }} />
        </Toolbar>
      </AppBar>
    );
  }

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: "#FFFFFF",
          borderBottom: "1px solid #E5E7EB",
          boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
        }}
      >
        <Toolbar
          sx={{
            minHeight: { xs: "56px", sm: "64px" },
            "@media (min-width:0px)": { minHeight: { xs: "56px", sm: "64px" } },
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: { xs: 0.5, sm: 1, md: 2 },
            px: { xs: 1, sm: 2, md: 3 },
          }}
        >
          {/* Left Section - Logo */}
          <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
            {isMobile && (
              <IconButton onClick={toggleMobileMenu} edge="start" sx={{ mr: { xs: 0.25, sm: 1 }, p: 0.5 }}>
                <MenuIcon sx={{ fontSize: "22px" }} />
              </IconButton>
            )}

            <Typography
              component={RouterLink}
              to="/"
              sx={{
                display: "flex",
                alignItems: "center",
                textDecoration: "none",
              }}
            >
              <Box
                component="img"
                src="/images/logo.png" // 👉 yaha apna logo path daalo
                alt="Shopmart Logo"
                sx={{
                  height: { xs: 30, sm: 35, md: 45 },
                  width: "auto",
                  maxWidth: { xs: 110, sm: "none" },
                  objectFit: "contain",
                  cursor: "pointer",
                }}
              />
            </Typography>
          </Box>

          {/* Center Section - Search Bar */}
          <Box
            component="form"
            onSubmit={handleSearch}
            sx={{
              flex: "1 1 auto",
              minWidth: 0,
              display: "flex",
              justifyContent: "center",
              position: "relative",
            }}
          >
            <Search>
              <SearchIconWrapper>
                <SearchIcon sx={{ fontSize: "18px" }} />
              </SearchIconWrapper>
              <StyledInputBase
                style={{ color: "black" }}
                placeholder={isXs ? "Search..." : "Search for products, brands and more..."}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              />
              {searchValue && (
                <ClearButton size="small" onClick={handleClearSearch}>
                  <CloseIcon sx={{ fontSize: "16px" }} />
                </ClearButton>
              )}
            </Search>

            {showSuggestions && searchValue && !isMobile && (
              <Paper
                elevation={2}
                sx={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  mt: 1,
                  zIndex: 1000,
                  borderRadius: 2,
                  overflow: "hidden",
                  border: "1px solid #E5E7EB",
                }}
              >
                <Box sx={{ p: 2 }}>
                  <Typography sx={{ fontSize: "11px", fontWeight: 600, color: "#6B7280", mb: 1, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    Popular Searches
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {popularSearches.map((item) => (
                      <Chip
                        key={item}
                        label={item}
                        size="small"
                        onClick={() => handleSuggestionClick(item)}
                        clickable
                        sx={{
                          fontSize: "12px",
                          height: "32px",
                          "&:hover": {
                            backgroundColor: alpha("#3B82F6", 0.1),
                            color: "#3B82F6",
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              </Paper>
            )}
          </Box>

          {/* Right Section - Navigation Icons */}
          <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.25, sm: 1 }, flexShrink: 0 }}>
            {!isMobile && (
              <>
                <Button component={RouterLink} to="/" sx={{ textTransform: "none", fontSize: "13px", color: "#374151", fontWeight: 500 }}>
                  Home
                </Button>
                <Button component={RouterLink} to="/products" sx={{ textTransform: "none", fontSize: "13px", color: "#374151", fontWeight: 500 }}>
                  Products
                </Button>
                <Button component={RouterLink} to="/offers" sx={{ textTransform: "none", fontSize: "13px", color: "#374151", fontWeight: 500 }}>
                  Offers
                </Button>
              </>
            )}

            <Tooltip title="Wishlist">
              <IconButton component={RouterLink} to="/wishlist" sx={{ color: "#374151", p: { xs: 0.75, sm: 1 } }}>
                <Badge badgeContent={wishlistCount} color="error" sx={{ "& .MuiBadge-badge": { fontSize: "10px", height: "18px", minWidth: "18px" } }}>
                  <FavoriteIcon sx={{ fontSize: { xs: "18px", sm: "20px" } }} />
                </Badge>
              </IconButton>
            </Tooltip>

            <Tooltip title="Cart">
              <IconButton component={RouterLink} to="/cart" sx={{ color: "#374151", p: { xs: 0.75, sm: 1 } }}>
                <Badge badgeContent={cartCount} color="error" sx={{ "& .MuiBadge-badge": { fontSize: "10px", height: "18px", minWidth: "18px" } }}>
                  <CartIcon sx={{ fontSize: { xs: "18px", sm: "20px" } }} />
                </Badge>
              </IconButton>
            </Tooltip>

            {isLoggedIn && user ? (
              <Tooltip title={`Hello, ${userFirstName}`}>
                <Button
                  onClick={handleProfileMenuOpen}
                  sx={{
                    textTransform: "none",
                    color: "#374151",
                    fontWeight: 500,
                    fontSize: "13px",
                    ml: { xs: 0.25, sm: 1 },
                    minWidth: "auto",
                    px: { xs: 0.5, sm: 1 },
                    "&:hover": { bgcolor: "transparent" },
                  }}
                >
                  {!isXs && (
                    <Typography sx={{ fontSize: "13px", fontWeight: 500, mr: 0.5 }}>
                      Hi, {userFirstName}
                    </Typography>
                  )}
                  <Avatar sx={{ width: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 }, bgcolor: "#3B82F6", fontSize: "13px" }}>
                    {userInitial}
                  </Avatar>
                </Button>
              </Tooltip>
            ) : (
              <Button
                variant="contained"
                startIcon={!isXs ? <LoginIcon sx={{ fontSize: "16px" }} /> : null}
                onClick={handleLoginMenuOpen}
                sx={{
                  ml: { xs: 0.25, sm: 1 },
                  borderRadius: 2,
                  minWidth: { xs: "40px", sm: "auto" },
                  px: { xs: 1, sm: 3 },
                  py: 0.8,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "13px",
                  bgcolor: "#3B82F6",
                  "&:hover": { bgcolor: "#2563EB" },
                }}
              >
                {isXs ? <LoginIcon sx={{ fontSize: "18px" }} /> : "Sign In"}
              </Button>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* CATEGORY MENU */}
      <Menu
        anchorEl={categoryAnchorEl}
        open={Boolean(categoryAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 2,
          sx: { mt: 0.5, minWidth: 200, borderRadius: 2, border: "1px solid #E5E7EB" },
        }}
      >
        {categories.map((cat) => (
          <MenuItem key={cat.name} onClick={() => navigateTo(cat.path)} sx={{ fontSize: "13px", py: 1 }}>
            <ListItemIcon sx={{ minWidth: 36 }}>{cat.icon}</ListItemIcon>
            <ListItemText primary={cat.name} primaryTypographyProps={{ fontSize: "13px" }} />
          </MenuItem>
        ))}
      </Menu>

      {/* PROFILE MENU */}
      <Menu
        anchorEl={anchorEl}
        open={isLoggedIn && Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 2,
          sx: { mt: 0.5, minWidth: 220, borderRadius: 2, border: "1px solid #E5E7EB" },
        }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #E5E7EB", bgcolor: "#F9FAFB" }}>
          <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#111827" }}>{user?.name || "User"}</Typography>
        </Box>
        <MenuItem onClick={() => navigateTo("/profile")} sx={{ fontSize: "13px", py: 1 }}>
          <ListItemIcon><PersonIcon sx={{ fontSize: "18px" }} /></ListItemIcon>
          <ListItemText primary="Profile" primaryTypographyProps={{ fontSize: "13px" }} />
        </MenuItem>
        <MenuItem onClick={() => navigateTo("/orders")} sx={{ fontSize: "13px", py: 1 }}>
          <ListItemIcon><HistoryIcon sx={{ fontSize: "18px" }} /></ListItemIcon>
          <ListItemText primary="Orders" primaryTypographyProps={{ fontSize: "13px" }} />
        </MenuItem>
        <MenuItem onClick={() => navigateTo("/wishlist")} sx={{ fontSize: "13px", py: 1 }}>
          <ListItemIcon><FavoriteIcon sx={{ fontSize: "18px" }} /></ListItemIcon>
          <ListItemText primary="Wishlist" primaryTypographyProps={{ fontSize: "13px" }} />
        </MenuItem>
        <MenuItem onClick={() => navigateTo("/settings")} sx={{ fontSize: "13px", py: 1 }}>
          <ListItemIcon><SettingsIcon sx={{ fontSize: "18px" }} /></ListItemIcon>
          <ListItemText primary="Settings" primaryTypographyProps={{ fontSize: "13px" }} />
        </MenuItem>
        <Divider />
        <MenuItem sx={{ fontSize: "13px", py: 1, color: "#EF4444" }}>
          <LogoutButton onClick={handleLogoutClick} primary="Logout" primaryTypographyProps={{ fontSize: "13px" }} />
        </MenuItem>
      </Menu>

      {/* LOGIN MENU */}
      <Menu
        anchorEl={loginAnchorEl}
        open={Boolean(loginAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 2,
          sx: { mt: 0.5, minWidth: 220, borderRadius: 2, border: "1px solid #E5E7EB" },
        }}
      >
        <MenuItem onClick={() => navigateTo("/login")} sx={{ fontSize: "13px", py: 1 }}>
          <ListItemIcon><LoginIcon sx={{ fontSize: "18px" }} /></ListItemIcon>
          <ListItemText primary="Sign In" primaryTypographyProps={{ fontSize: "13px" }} />
        </MenuItem>
        <MenuItem onClick={() => navigateTo("/signup")} sx={{ fontSize: "13px", py: 1 }}>
          <ListItemIcon><PersonIcon sx={{ fontSize: "18px" }} /></ListItemIcon>
          <ListItemText primary="Create Account" primaryTypographyProps={{ fontSize: "13px" }} />
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => navigateTo("/support")} sx={{ fontSize: "13px", py: 1 }}>
          <ListItemIcon><SupportIcon sx={{ fontSize: "18px" }} /></ListItemIcon>
          <ListItemText primary="Need Help?" primaryTypographyProps={{ fontSize: "13px" }} />
        </MenuItem>
      </Menu>

      {/* MOBILE DRAWER */}
      <Drawer anchor="left" open={mobileMenuOpen} onClose={toggleMobileMenu}>
        <Box sx={{ width: 280 }}>
          <Box
            sx={{ p: 2.5, bgcolor: "#3B82F6", color: "white", cursor: "pointer" }}
            onClick={() => navigateTo("/")}
          >
            <Typography sx={{ fontSize: "16px", fontWeight: 600 }}>{isLoggedIn && user ? user.name : "Welcome!"}</Typography>
            <Typography sx={{ fontSize: "12px", opacity: 0.9 }}>{isLoggedIn && user ? user.email : "Please login to continue"}</Typography>
          </Box>

          <List sx={{ py: 1.5 }}>
            <ListItem button onClick={() => navigateTo("/")} sx={{ py: 1.2 }}>
              <ListItemIcon><HomeIcon sx={{ fontSize: "20px" }} /></ListItemIcon>
              <ListItemText primary="Home" primaryTypographyProps={{ fontSize: "14px" }} />
            </ListItem>
            <ListItem button onClick={() => navigateTo("/products")} sx={{ py: 1.2 }}>
              <ListItemIcon><CategoryIcon sx={{ fontSize: "20px" }} /></ListItemIcon>
              <ListItemText primary="Products" primaryTypographyProps={{ fontSize: "14px" }} />
            </ListItem>
            <ListItem button onClick={() => navigateTo("/offers")} sx={{ py: 1.2 }}>
              <ListItemIcon><OfferIcon sx={{ fontSize: "20px" }} /></ListItemIcon>
              <ListItemText primary="Offers" primaryTypographyProps={{ fontSize: "14px" }} />
            </ListItem>
            <ListItem button onClick={() => navigateTo("/support")} sx={{ py: 1.2 }}>
              <ListItemIcon><SupportIcon sx={{ fontSize: "20px" }} /></ListItemIcon>
              <ListItemText primary="Support" primaryTypographyProps={{ fontSize: "14px" }} />
            </ListItem>

            <Divider sx={{ my: 1.5 }} />

            {isLoggedIn ? (
              <>
                <ListItem button onClick={() => navigateTo("/profile")} sx={{ py: 1.2 }}>
                  <ListItemIcon><PersonIcon sx={{ fontSize: "20px" }} /></ListItemIcon>
                  <ListItemText primary="Profile" primaryTypographyProps={{ fontSize: "14px" }} />
                </ListItem>
                <ListItem button onClick={() => navigateTo("/wishlist")} sx={{ py: 1.2 }}>
                  <ListItemIcon><FavoriteIcon sx={{ fontSize: "20px" }} /></ListItemIcon>
                  <ListItemText primary={`Wishlist (${wishlistCount})`} primaryTypographyProps={{ fontSize: "14px" }} />
                </ListItem>
                <ListItem button onClick={() => navigateTo("/orders")} sx={{ py: 1.2 }}>
                  <ListItemIcon><HistoryIcon sx={{ fontSize: "20px" }} /></ListItemIcon>
                  <ListItemText primary="Orders" primaryTypographyProps={{ fontSize: "14px" }} />
                </ListItem>
                <ListItem sx={{ py: 1.2 }}>
                  <ListItemIcon><LogoutIcon sx={{ fontSize: "20px" }} /></ListItemIcon>
                  <LogoutButton primary="Logout" primaryTypographyProps={{ fontSize: "14px" }} />
                </ListItem>
              </>
            ) : (
              <>
                <ListItem button onClick={() => navigateTo("/login")} sx={{ py: 1.2 }}>
                  <ListItemIcon><LoginIcon sx={{ fontSize: "20px" }} /></ListItemIcon>
                  <ListItemText primary="Login" primaryTypographyProps={{ fontSize: "14px" }} />
                </ListItem>
                <ListItem button onClick={() => navigateTo("/signup")} sx={{ py: 1.2 }}>
                  <ListItemIcon><PersonIcon sx={{ fontSize: "20px" }} /></ListItemIcon>
                  <ListItemText primary="Sign Up" primaryTypographyProps={{ fontSize: "14px" }} />
                </ListItem>
              </>
            )}
          </List>
        </Box>
      </Drawer>
    </>
  );
};

export default Navbar;